import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import {
  GridFilterOperator,
  GridFilterItem,
  GridFilterInputValueProps,
} from "@mui/x-data-grid-premium";

import {
  BUYBOX_SELLER_IMAGE,
  BUYBOX_CONDITION_IMAGE,
  BUYBOX_SHIP_SPEED_IMAGE,
} from "../../formatting/buyboxInfo";

interface BuyBoxFilterOption {
  label: string;
  value: string; // search token sent to backend
  icon: string | null;
}

const BUYBOX_FILTER_OPTIONS: BuyBoxFilterOption[] = [
  // Seller Statuses
  { label: "YOM", value: "- yom", icon: BUYBOX_SELLER_IMAGE.yom },
  { label: "AMAZON", value: "amazon", icon: BUYBOX_SELLER_IMAGE.amazon },
  { label: "NOT YOM", value: "not yom", icon: null },
  // Fulfillment
  { label: "FBA", value: "fba", icon: BUYBOX_CONDITION_IMAGE.fba },
  { label: "MERCHANT", value: "merchant", icon: BUYBOX_CONDITION_IMAGE.merchant },
  // FBA Handling Time
  { label: "FAST", value: "fast", icon: BUYBOX_SHIP_SPEED_IMAGE.fast },
  { label: "MEDIUM", value: "medium", icon: BUYBOX_SHIP_SPEED_IMAGE.medium },
  { label: "SLOW", value: "slow", icon: BUYBOX_SHIP_SPEED_IMAGE.slow },
  {
    label: "TEMP OUT OF STOCK",
    value: "temp out of stock",
    icon: BUYBOX_CONDITION_IMAGE["temp out of stock"],
  },
  // Other
  { label: "INELIGIBLE", value: "ineligible", icon: BUYBOX_CONDITION_IMAGE["ineligible - new"] },
  { label: "SUPPRESSED", value: "suppressed", icon: BUYBOX_CONDITION_IMAGE.suppressed },
  { label: "USED", value: "used", icon: BUYBOX_CONDITION_IMAGE.used },
  { label: "NO OFFERS", value: "no offers", icon: BUYBOX_CONDITION_IMAGE["no offers"] },
];

function BuyBoxInfoFilterInput({
  item,
  applyValue,
}: GridFilterInputValueProps) {
  const currentValues = (item.value as string[]) || [];

  const selectedOptions = BUYBOX_FILTER_OPTIONS.filter((opt) =>
    currentValues.includes(opt.value)
  );

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: BuyBoxFilterOption[]
  ) => {
    applyValue({ ...item, value: newValue.map((opt) => opt.value) });
  };

  return (
    <Autocomplete
      multiple
      options={BUYBOX_FILTER_OPTIONS}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderOption={(props, option) => (
        <li {...props}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {option.icon ? (
              <img
                src={option.icon}
                alt={option.label}
                style={{ width: 20, height: 20 }}
              />
            ) : null}
            <span style={{ fontSize: "0.85rem" }}>{option.label}</span>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Value"
          placeholder="Select options..."
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip
              key={key}
              size="small"
              {...rest}
              sx={{ height: 20, fontSize: "0.75rem" }}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {option.icon ? (
                    <img
                      src={option.icon}
                      alt={option.label}
                      style={{ width: 14, height: 14 }}
                    />
                  ) : null}
                  <span>{option.label}</span>
                </Box>
              }
            />
          );
        })
      }
      sx={{ minWidth: 200 }}
      disableCloseOnSelect
    />
  );
}

export function getBuyBoxInfoFilterOperators(): GridFilterOperator[] {
  return [
    {
      label: "contains any of",
      value: "containsAny",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0
        ) {
          return null;
        }
        const filterValues = filterItem.value as string[];
        return (cellValue: string | null | undefined) => {
          if (!cellValue) return false;
          const lower = cellValue.toLowerCase();
          return filterValues.some((token) => lower.includes(token));
        };
      },
      InputComponent: BuyBoxInfoFilterInput,
    },
    {
      label: "contains all of",
      value: "containsAll",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0
        ) {
          return null;
        }
        const filterValues = filterItem.value as string[];
        return (cellValue: string | null | undefined) => {
          if (!cellValue) return false;
          const lower = cellValue.toLowerCase();
          return filterValues.every((token) => lower.includes(token));
        };
      },
      InputComponent: BuyBoxInfoFilterInput,
    },
    {
      label: "does not contain",
      value: "doesNotContain",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0
        ) {
          return null;
        }
        const filterValues = filterItem.value as string[];
        return (cellValue: string | null | undefined) => {
          if (!cellValue) return false;
          const lower = cellValue.toLowerCase();
          return filterValues.every((token) => !lower.includes(token));
        };
      },
      InputComponent: BuyBoxInfoFilterInput,
    },
    {
      label: "is empty",
      value: "isEmpty",
      getApplyFilterFn: () => {
        return (cellValue: string | null | undefined): boolean => {
          return !cellValue || cellValue.length === 0;
        };
      },
      requiresFilterValue: false,
    },
    {
      label: "is not empty",
      value: "isNotEmpty",
      getApplyFilterFn: () => {
        return (cellValue: string | null | undefined): boolean => {
          return Boolean(cellValue && cellValue.length > 0);
        };
      },
      requiresFilterValue: false,
    },
  ];
}
